import Vimeo from "@vimeo/player";
import { cn } from "@babybjorn/ui";
import { useEffect, useRef, useState, type FC } from "react";
import { getOriginalVimeoThumbnail } from "../../utils/vimeo/utils";
import * as s from "./VimeoPlayer.module.scss";

type VimeoOptions = Vimeo.Options & { vimeo_logo?: boolean };

type VimeoPlayerProps = {
	videoUrl?: string | undefined;
	videoID: number;
	playerID: string;
	title?: string;
	posterUrl?: string;
	width?: number;
	height?: number;
	settings?: VimeoOptions;
};

const VimeoPlayer: FC<VimeoPlayerProps> = ({
	videoID,
	playerID,
	title,
	posterUrl,
	width,
	height,
	settings,
}) => {
	const [loaded, setLoaded] = useState(false);
	const settingsRef = useRef(settings);
	const originalThumbnail = posterUrl && getOriginalVimeoThumbnail(posterUrl);

	useEffect(() => {
		const player = new Vimeo(playerID, {
			id: videoID,
			autoplay: true,
			loop: true,
			controls: false,
			playsinline: true,
			muted: true,
			title: false,
			byline: false,
			responsive: true,
			transparent: true,
			autopause: false,
			background: true,
			vimeo_logo: false,
			...settingsRef.current,
		});

		//If there is any sort of error, destroy player.
		player.ready().catch(() => {
			void player.destroy();
		});

		player.on("play", () => {
			setTimeout(() => {
				setLoaded(true);
			}, 100);
		});

		player.on("error", () => {
			setLoaded(false);
		});

		return () => {
			//Remove player on clean up
			player.destroy().catch(() => console.error("Error removing player"));
		};
	}, [playerID, videoID]);

	return (
		<div
			className={cn(s.wrapper)}
			style={{ aspectRatio: `${width}/${height}` }}
		>
			{posterUrl && (
				<img
					alt={title ?? "Vimeo Video"}
					loading="lazy"
					src={originalThumbnail ?? posterUrl}
					className={cn(s.placeholder, { [s.loaded]: loaded })}
				/>
			)}
			<div
				id={playerID}
				className={cn(s.videoPlayer, {
					[s.loaded]: loaded,
				})}
			></div>
		</div>
	);
};
export default VimeoPlayer;
